import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  QUICK_PERIODS, getQuickRange, BILLABLE_STATUSES,
  getDisplayStatus,
} from '../../utils/reportHelpers';

// ── Location lookup ───────────────────────────────────────────────────────────

const LOCATIONS = {
  SYD: { lat: -33.9, lon: 151.2 },
  MEL: { lat: -37.7, lon: 144.8 },
  BNE: { lat: -27.4, lon: 153.1 },
  PER: { lat: -31.9, lon: 115.9 },
  ADL: { lat: -34.9, lon: 138.5 },
  CBR: { lat: -35.3, lon: 149.2 },
  DRW: { lat: -12.4, lon: 130.9 },
  HBA: { lat: -42.8, lon: 147.5 },
  CNS: { lat: -16.9, lon: 145.8 },
  TSV: { lat: -19.2, lon: 146.8 },
  OOL: { lat: -28.2, lon: 153.5 },
  MKY: { lat: -21.2, lon: 149.2 },
  SIN: { lat:   1.4, lon: 103.9 },
  NRT: { lat:  35.8, lon: 140.4 },
  LAX: { lat:  33.9, lon: -118.4 },
  LHR: { lat:  51.5, lon:  -0.5 },
  DXB: { lat:  25.2, lon:  55.4 },
  HKG: { lat:  22.3, lon: 113.9 },
  BKK: { lat:  13.7, lon: 100.7 },
  AKL: { lat: -37.0, lon: 174.8 },
};

// City name → IATA code mapping (case-insensitive)
const CITY_TO_CODE = {
  sydney:       'SYD',
  melbourne:    'MEL',
  brisbane:     'BNE',
  perth:        'PER',
  adelaide:     'ADL',
  canberra:     'CBR',
  darwin:       'DRW',
  hobart:       'HBA',
  cairns:       'CNS',
  townsville:   'TSV',
  'gold coast': 'OOL',
  mackay:       'MKY',
  singapore:    'SIN',
  tokyo:        'NRT',
  'los angeles':'LAX',
  london:       'LHR',
  dubai:        'DXB',
  'hong kong':  'HKG',
  bangkok:      'BKK',
  auckland:     'AKL',
};

function findLocation(codeOrCity) {
  if (!codeOrCity) return null;
  const upper = codeOrCity.trim().toUpperCase();
  if (LOCATIONS[upper]) return LOCATIONS[upper];
  const lower = codeOrCity.trim().toLowerCase();
  const mapped = CITY_TO_CODE[lower];
  if (mapped) return LOCATIONS[mapped];
  return null;
}

// ── DEFRA 2023 emission factors (kg CO2e / pax-km) ───────────────────────────

const FACTORS = {
  economy_short:  0.255,
  economy_long:   0.195,
  business_short: 0.510,
  business_long:  0.391,
  first_short:    0.765,
  first_long:     0.585,
};

const LONG_HAUL_KM = 3700;

// ── Haversine ─────────────────────────────────────────────────────────────────

function haversineKm(lat1, lon1, lat2, lon2) {
  const R  = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

// ── Sector CO2 ────────────────────────────────────────────────────────────────

function sectorCO2kg(sector) {
  if (sector.type !== 'flight') return 0;
  const from = findLocation(sector.from);
  const to   = findLocation(sector.to);
  if (!from || !to) return null; // unknown route
  const dist   = haversineKm(from.lat, from.lon, to.lat, to.lon);
  const isLong = dist >= LONG_HAUL_KM;
  const cls    = (sector.cabinClass || 'Economy').toLowerCase();
  let factor;
  if (cls.includes('business')) {
    factor = isLong ? FACTORS.business_long : FACTORS.business_short;
  } else if (cls.includes('first')) {
    factor = isLong ? FACTORS.first_long : FACTORS.first_short;
  } else {
    factor = isLong ? FACTORS.economy_long : FACTORS.economy_short;
  }
  return dist * factor;
}

// Route key
function routeKey(sector) {
  return `${(sector.from || '?').toUpperCase()} → ${(sector.to || '?').toUpperCase()}`;
}

// Month label from "YYYY-MM-DD"
function monthKey(dateStr) {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CarbonReport({ trips, clientConfig }) {
  const now = new Date();
  const fy  = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;

  const [periodKey, setPeriodKey] = useState('thisFY');
  const [from,      setFrom]      = useState(`${fy}-07-01`);
  const [to,        setTo]        = useState(`${fy + 1}-06-30`);

  const enabled = clientConfig?.features?.carbonTracking === true;

  const applyPreset = (key) => {
    setPeriodKey(key);
    if (key === 'custom') return;
    const r = getQuickRange(key);
    setFrom(r.from);
    setTo(r.to);
  };

  // All hooks must run unconditionally — feature-flag gate happens in JSX below

  // Filtered trips: billable, in period, has at least one flight sector
  const filteredTrips = useMemo(() => {
    if (!enabled) return [];
    return trips.filter(t => {
      const ds = getDisplayStatus(t);
      if (!BILLABLE_STATUSES.has(ds)) return false;
      if (from && (t.startDate || '') < from) return false;
      if (to   && (t.startDate || '') > to)   return false;
      return (t.sectors || []).some(s => s.type === 'flight');
    });
  }, [enabled, trips, from, to]);

  // Per-trip CO2 and unknown-route tracking
  const tripStats = useMemo(() => {
    return filteredTrips.map(trip => {
      const flightSectors = (trip.sectors || []).filter(s => s.type === 'flight');
      let totalKg     = 0;
      const unknown   = [];
      flightSectors.forEach(s => {
        const kg = sectorCO2kg(s);
        if (kg === null) {
          unknown.push(routeKey(s));
        } else {
          totalKg += kg;
        }
      });
      return { trip, totalKg, unknown };
    });
  }, [filteredTrips]);

  // Summary
  const summary = useMemo(() => {
    const totalKg   = tripStats.reduce((s, r) => s + r.totalKg, 0);
    const tripCount = tripStats.length;
    const avgKg     = tripCount > 0 ? totalKg / tripCount : 0;
    const unknownRoutes = new Set();
    tripStats.forEach(r => r.unknown.forEach(rt => unknownRoutes.add(rt)));
    return { totalKg, tripCount, avgKg, unknownRouteCount: unknownRoutes.size, unknownRoutes: [...unknownRoutes].sort() };
  }, [tripStats]);

  // Bar chart: tCO2e by month
  const chartData = useMemo(() => {
    const map = {};
    tripStats.forEach(({ trip, totalKg }) => {
      const mk = monthKey(trip.startDate);
      const sk = (trip.startDate || '').slice(0, 7);
      if (!map[mk]) map[mk] = { month: mk, tco2e: 0, sortKey: sk };
      map[mk].tco2e += totalKg / 1000;
    });
    return Object.values(map).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [tripStats]);

  // Top 10 routes by total CO2e
  const topRoutes = useMemo(() => {
    const map = {};
    tripStats.forEach(({ trip }) => {
      (trip.sectors || []).filter(s => s.type === 'flight').forEach(s => {
        const kg = sectorCO2kg(s);
        if (kg === null) return;
        const key = routeKey(s);
        if (!map[key]) map[key] = { route: key, trips: 0, totalKg: 0 };
        map[key].trips++;
        map[key].totalKg += kg;
      });
    });
    return Object.values(map)
      .sort((a, b) => b.totalKg - a.totalKg)
      .slice(0, 10);
  }, [tripStats]);

  const fmtTco2e = (kg) => (kg / 1000).toFixed(2);

  // Feature flag gate — rendered after all hooks
  if (!enabled) {
    return (
      <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '48px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>♻️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
          Carbon Tracking not enabled
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', maxWidth: 400, margin: '0 auto' }}>
          Enable carbon tracking in client settings to use this report.
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* Filters */}
      <div style={card}>
        <div>
          <label style={lbl}>Period</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {QUICK_PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                style={{
                  ...pill,
                  background:  periodKey === p.key ? '#0d9488' : undefined,
                  color:       periodKey === p.key ? '#fff'    : undefined,
                  borderColor: periodKey === p.key ? '#0d9488' : undefined,
                }}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => applyPreset('custom')}
              style={{
                ...pill,
                background:  periodKey === 'custom' ? '#0d9488' : undefined,
                color:       periodKey === 'custom' ? '#fff'    : undefined,
                borderColor: periodKey === 'custom' ? '#0d9488' : undefined,
              }}
            >
              Custom
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div>
              <label style={lbl}>From</label>
              <input
                type="date" value={from}
                onChange={e => { setFrom(e.target.value); setPeriodKey('custom'); }}
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>To</label>
              <input
                type="date" value={to}
                onChange={e => { setTo(e.target.value); setPeriodKey('custom'); }}
                style={inp}
              />
            </div>
          </div>
        </div>
      </div>

      {filteredTrips.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#78716c' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>—</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#57534e' }}>
            No flight data in this period.
          </div>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 20 }}>
            <div style={summaryCard}>
              <div style={summaryLabel}>Total CO2e</div>
              <div style={summaryNumber}>{fmtTco2e(summary.totalKg)} tCO2e</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Flight trips counted</div>
              <div style={summaryNumber}>{summary.tripCount}</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Avg CO2e per trip</div>
              <div style={summaryNumber}>{Math.round(summary.avgKg)} kg</div>
            </div>
            <div style={{
              ...summaryCard,
              borderColor: summary.unknownRouteCount > 0 ? '#fde68a' : '#e2e8f0',
              background:  summary.unknownRouteCount > 0 ? '#fffbeb' : '#fff',
            }}>
              <div style={summaryLabel}>Routes with unknown distances</div>
              <div style={{ ...summaryNumber, color: summary.unknownRouteCount > 0 ? '#d97706' : '#0f172a' }}>
                {summary.unknownRouteCount}
              </div>
            </div>
          </div>

          {/* Bar chart */}
          {chartData.length > 0 && (
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={sectionHeading}>tCO2e by Month</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => `${v.toFixed(1)}t`}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value) => [`${value.toFixed(2)} tCO2e`, 'Emissions']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="tco2e" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top 10 routes table */}
          {topRoutes.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={sectionHeading}>Top 10 Routes by Emissions</div>
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th scope="col" style={th}>Route</th>
                        <th scope="col" style={{ ...th, textAlign: 'right' }}>Trips</th>
                        <th scope="col" style={{ ...th, textAlign: 'right' }}>Total CO2e (tCO2e)</th>
                        <th scope="col" style={{ ...th, textAlign: 'right' }}>Avg CO2e (kg/trip)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRoutes.map((row, idx) => (
                        <tr
                          key={row.route}
                          style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}
                        >
                          <td style={{ ...td, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                            {row.route}
                          </td>
                          <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{row.trips}</td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>
                            {fmtTco2e(row.totalKg)}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>
                            {Math.round(row.totalKg / row.trips)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Unknown routes notice */}
          {summary.unknownRouteCount > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#d97706', marginBottom: 6 }}>
                {summary.unknownRouteCount} route{summary.unknownRouteCount !== 1 ? 's' : ''} could not be estimated — airport codes not in the lookup table.
              </div>
              <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.6 }}>
                Unknown routes: {summary.unknownRoutes.join(', ')}
              </div>
            </div>
          )}

          {/* Attribution note */}
          <p style={{ fontSize: 11, color: '#78716c', fontStyle: 'italic', marginTop: 8, lineHeight: 1.6 }}>
            Emission factors: DEFRA 2023. Methodology: distance &times; passenger emission factor (economy/business/first class,
            short-haul &lt;3,700&nbsp;km / long-haul &ge;3,700&nbsp;km).
            Accommodation and ground transport emissions are not included.
          </p>
        </>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const card          = { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl           = { display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const inp           = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff' };
const pill          = { padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f1f5f9', color: '#475569', fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const summaryCard   = { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const summaryLabel  = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const summaryNumber = { fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 };
const sectionHeading= { fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 };
const th            = { padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const td            = { padding: '10px 12px', color: '#374151' };
